import styled, { css } from 'styled-components';
import { Clock16 } from '@elfsight/icons';
import { Icon } from '@elfsight/lib-icon';
import { createReadableClass } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { DividerBlock } from '../components/divider-block';
import { LinkButton } from '../components/link-button';
import { Body1 } from '../components/typography';
import { usePaymentData } from '../payment/payment-data-provider';
import { useDatetimeFormat } from '../utils/use-datetime-format';
import { useWidgetDimensions } from '../widget-dimensions-provider';
import { CurrentDay } from './current-day';
import { useDatetime } from './datetime-provider';

type CurrentTimeslotProps = {
  canChange?: boolean;
};

export function CurrentTimeslot({ canChange }: CurrentTimeslotProps) {
  const { selectedTimeslot, setSelectedTimeslot } = useDatetime();
  const { t } = useLingui();
  const { formatTime } = useDatetimeFormat();
  const { textColor } = useSettings();
  const { isMobile } = useWidgetDimensions();
  const { isPaymentInProcess } = usePaymentData();

  if (!selectedTimeslot) {
    return null;
  }

  return (
    <Container _disabled={isPaymentInProcess}>
      <DateTime>
        <CurrentDay />
        <TimeRow>
          <Icon size={16} component={Clock16} fill={textColor} />
          <Time _isMobile={isMobile}>
            {formatTime(selectedTimeslot.from)} -{' '}
            {formatTime(selectedTimeslot.to)}
          </Time>
        </TimeRow>
      </DateTime>
      {canChange && (
        <LinkButton onClick={() => setSelectedTimeslot(undefined)}>
          {t`Change`}
        </LinkButton>
      )}
    </Container>
  );
}

const Container = styled(DividerBlock).attrs({
  className: createReadableClass({
    component: 'current-timeslot',
    element: 'container'
  })
})<{
  _disabled?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  padding: 20px 0;

  ${({ _disabled = false }) =>
    _disabled &&
    css`
      pointer-events: none;
    `}
`;

const DateTime = styled.div.attrs({
  className: createReadableClass({
    component: 'current-timeslot',
    element: 'date-time'
  })
})`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TimeRow = styled.div.attrs({
  className: createReadableClass({
    component: 'current-timeslot',
    element: 'time-row'
  })
})`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Time = styled(Body1).attrs({
  className: createReadableClass({
    component: 'current-timeslot',
    element: 'time'
  })
})<{ _isMobile: boolean }>`
  ${({ _isMobile }) =>
    _isMobile &&
    css`
      font-size: 14px;
    `}
`;
